import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button from 'app/components/Button/ButtonVariant';
import { Session } from '../services/api';
import { generateCurrencySymbol } from 'app/routes/BuildProgram/Pricing/utils';
import { formatAmount } from 'app/utils/currency';

interface PrivateSessionsProps {
  sessions: Session[];
  showViewAll: boolean | undefined;
  onViewAll?: () => void;
  publicCalendarUrl?: string;
  currency?: string;
  /**
   * Books the session in the app instead of sending the browser to the coach's
   * public calendar. Only the signed-in coach page passes it; without it this
   * behaves exactly as it did.
   */
  onBook?: (session: Session) => void;
}

const PrivateSessions: React.FC<PrivateSessionsProps> = ({
  sessions,
  showViewAll,
  onViewAll,
  publicCalendarUrl,
  currency,
  onBook
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const shouldShowViewAll = showViewAll;
  const displayedSessions = showViewAll ? sessions.slice(0, 3) : sessions;

  // State for expanded/truncated sessions
  const [expandedSessions, setExpandedSessions] = useState<Set<number>>(new Set());
  const [truncatedSessions, setTruncatedSessions] = useState<Set<number>>(new Set());
  const descriptionRefs = useRef<{ [key: number]: HTMLParagraphElement | null }>({});

  // Check if session description is truncated
  const checkSessionTruncation = (sessionId: number) => {
    const element = descriptionRefs.current[sessionId];
    if (element) {
      // Check BOTH horizontal (desktop) and vertical (mobile) truncation
      const isHorizontallyOverflowing = element.scrollWidth > element.clientWidth;
      const isVerticallyOverflowing = element.scrollHeight > element.clientHeight;
      const isOverflowing = isHorizontallyOverflowing || isVerticallyOverflowing;

      if (isOverflowing) {
        setTruncatedSessions(prev => new Set(prev).add(sessionId));
      }
    }
  };

  // Toggle expanded state
  const toggleSessionExpanded = (sessionId: number) => {
    setExpandedSessions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sessionId)) {
        newSet.delete(sessionId);
      } else {
        newSet.add(sessionId);
      }
      return newSet;
    });
  };

  // Check truncation after render
  useEffect(() => {
    displayedSessions.forEach(session => {
      setTimeout(() => checkSessionTruncation(session.id), 10);
    });
  }, [displayedSessions]);

  /**
   * In the app, `onBook` books the session where the athlete stands — they are
   * signed in and already have a booking in progress.
   *
   * On the public page this opens the coach's calendar **on the option that was
   * just tapped**, which is the flow the frames draw: the coach's page lists what
   * they offer (526:35466), and the calendar arrives once one of them is picked
   * (CFTB-3430). It used to send the browser to the calendar page with no session
   * at all, which landed on that same list of options a second time — the athlete
   * chose their session, and was asked to choose it again.
   *
   * Two things make the difference between this and the deep link CFTB-3448
   * closed, and both are why it is a router push rather than `window.location`:
   *
   *   - `sessionPicked` rides on the history entry, so `openOnCalendar` can tell a
   *     session this browser settled from one a pasted URL merely names. A cold
   *     visit to the same address still gets the list.
   *   - the entry is a history entry, so Back returns to the coach's page. That
   *     page has no back arrow of its own.
   */
  const handleBookNow = (session: Session) => {
    if (onBook) {
      onBook(session);
      return;
    }

    if (!publicCalendarUrl) {
      console.warn('Public calendar URL not available');
      return;
    }

    // Pathname and query only: the calendar is a route of this same app, and
    // staying on the origin the visitor is already on keeps a preview build on
    // its own deployment rather than jumping to the configured public host.
    const target = new URL(publicCalendarUrl, window.location.origin);
    target.searchParams.set('sessionId', String(session.id));
    history.push(`${target.pathname}${target.search}`, { sessionPicked: true });
  };

  return (
    <div className="pub-section">
      {showViewAll && (
        <div className="pub-section-header">
          <h2 className="pub-section-heading">{t('Private Sessions')}</h2>
          {shouldShowViewAll && (
            <a
              href="#"
              className="pub-view-all-link"
              onClick={(e) => {
                e.preventDefault();
                if (onViewAll) {
                  onViewAll();
                }
              }}
            >
              {t('View All')}
            </a>
          )}
        </div>
      )}

      {sessions.length === 0 ? (
        <div className="pub-empty-state">
          <h3 className="pub-empty-state-title">{t('No Private Sessions Available')}</h3>
          <p className="pub-empty-state-description">{t('Check back soon for upcoming private training sessions.')}</p>
        </div>
      ) : (
        <>
          <div className={`sessions-grid ${showViewAll ? 'pub-sessions-grid-swipeable' : ''}`}>
            {displayedSessions.map((session) => (
              <div key={session.id} className="pub-session-card">
                <div className="pub-session-header">
                  <div className="pub-duration-badge">
                    <div className="pub-duration-number">{session.duration}</div>
                    <div className="pub-duration-unit">{t('min')}</div>
                  </div>
                  <div className="pub-session-title-wrapper">
                    <h3 className="pub-session-title">{session.title}</h3>
                    <div>
                      <p
                        ref={el => descriptionRefs.current[session.id] = el}
                        className={`pub-session-description ${
                          !expandedSessions.has(session.id) ? 'pub-session-description-collapsed' : ''
                        }`}
                      >
                        {session.description}
                      </p>
                      {(truncatedSessions.has(session.id) || expandedSessions.has(session.id)) && (
                        <button
                          className="pub-show-more-btn"
                          onClick={() => toggleSessionExpanded(session.id)}
                        >
                          {expandedSessions.has(session.id) ? t('Show Less') : t('Show More')}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                <div className="pub-session-footer-wrapper">
                  <hr className="pub-session-divider" />
                  <div className={`pub-session-footer${session.hidePrice ? ' pub-session-footer-no-price' : ''}`}>
                    {/* CN-2711: an option can be booked without its price on show. */}
                    {!session.hidePrice && (
                      <div className="pub-price-info">
                        {/* The amount alone (43593:28812): a "Price" caption over
                            it says what the number obviously is, and the frame
                            gives the line to the number instead. */}
                        <span className="pub-price-value">{generateCurrencySymbol(currency || 'USD')}{formatAmount(session.price)}</span>
                      </div>
                    )}
                    <Button buttonType="secondary-filled" title={t('Book Now')} onClickFunc={() => handleBookNow(session)}/>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default PrivateSessions;
